import { FlightError } from "@b2bportal/air-booking-api";
import {
  NO_AVAILABILITY_AIR,
  FlightBookType,
  ErrorTitles,
  ErrorModalType,
  GeneralPurchaseErrorEnum,
  PaymentPurchaseErrorEnum,
  PurchaseError,
  PaymentErrorEnum,
  UncategorizedPurchaseError,
} from "redmond";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  getAgentErrorTitleFromPurchaseError,
  getAgentErrorSubtitleFromPurchaseError,
} from "../selectors";
import * as textConstants from "../selectors/textConstants";

export const getErrorTitlesFromPaymentError = ({
  errors,
  numTravelers,
  flightBookType,
  hasSelectedSeats,
}: {
  errors: (PurchaseError | FlightError)[];
  numTravelers: number;
  flightBookType: FlightBookType;
  hasSelectedSeats: boolean;
}): ErrorTitles => {
  let agentSubtitle = getAgentErrorSubtitleFromPurchaseError(errors[0]);
  let agentTitle = getAgentErrorTitleFromPurchaseError(errors[0]);
  const errorTitles: ErrorTitles[] = errors.map((error) => {
    let titles: ErrorTitles = {
      title: textConstants.GENERIC_ERROR_TITLE,
      subtitle: textConstants.GENERIC_ERROR_SUBTITLE,
      icon: textConstants.ERROR_ICON,
    };

    const errorCode = (error as UncategorizedPurchaseError).code
      ? (error as UncategorizedPurchaseError).code
      : error;

    // Check for backend-provided custom messages for CardDeclined
    if (
      (error as UncategorizedPurchaseError).code === PaymentErrorEnum.CardDeclined
    ) {
      const uncategorized = error as UncategorizedPurchaseError;
      if (uncategorized.title && uncategorized.subtitle) {
        titles = {
          title: uncategorized.title,
          subtitle: uncategorized.subtitle,
          icon: textConstants.UNABLED_ICON,
          primaryButtonText: textConstants.EDIT_PAYMENT_METHOD,
        };
        return titles;
      }
    }

    switch (errorCode) {
      case FlightError.InvalidCustomer:
      case FlightError.NoAdultPassenger:
      case FlightError.IllegalLapInfantRedress:
      case FlightError.IllegalLapInfantKTN:
      case FlightError.LapInfantTooOld:
      case FlightError.TooManyLapInfants:
      case FlightError.NoContactInformation:
      case FlightError.TooManyPassengers:
      case FlightError.MalformedRedressNumber:
      case FlightError.MalformedKnownTravelerNumber:
      case FlightError.LapInfantsUnsupported:
      case FlightError.SeatedInfantsUnsupported:
      case FlightError.MissingPassport:
      case FlightError.InvalidPassengers:
        if (error === FlightError.NoAdultPassenger) {
          titles = {
            type: ErrorModalType.NO_ADULT_PASSENGER,
            title: textConstants.NO_ADULT_PAX_TITLE,
            subtitle: textConstants.NO_ADULT_PAX_SUBTITLE,
            primaryButtonText: textConstants.MODIFY_PAX,
          };
        } else {
          titles = {
            title: textConstants.INVALID_PASSENGERS,
            subtitle: textConstants.GENERIC_ERROR_SUBTITLE,
            primaryButtonText: textConstants.UPDATE_TRAVELERS,
          };
        }
        break;
      case FlightError.ExpiredPassport:
        titles = {
            title: textConstants.EXPIRED_PASSPORT,
            subtitle: textConstants.EXPIRED_PASSPORT_SUBTITLE,
            primaryButtonText: textConstants.UPDATE_TRAVELER_DETAILS,
          };
          break;
      case FlightError.InvalidPassportNumber: {
        titles = {
          title: textConstants.INVALID_PASSPORT_NUMBER,
          subtitle: textConstants.UPDATE_PASSPORT_INFO,
          primaryButtonText: textConstants.UPDATE_TRAVELERS,
        };
        break;
      }
      case PaymentPurchaseErrorEnum.RedemptionFailure:
        titles = {
          title: textConstants.REDEMPTION_ISSUE,
          subtitle: textConstants.REDEMPTION_ISSUE_SUTITLE,
          primaryButtonText: textConstants.TRY_AGAIN,
          icon: textConstants.UNABLED_ICON,
        };
        break;
      case FlightError.InvalidPassengers:
        titles = {
          title: textConstants.INVALID_PASSENGERS,
          subtitle: "",
          primaryButtonText: textConstants.UPDATE_TRAVELERS,
        };
        break;
      case GeneralPurchaseErrorEnum.FraudAutoReject:
      case GeneralPurchaseErrorEnum.LikelyFraud:
        titles = {
          title: textConstants.GENERIC_ERROR_TITLE,
          subtitle: textConstants.FRAUD_SUBTITLE,
          icon: textConstants.UNABLED_ICON,
          primaryButtonText: textConstants.EDIT_PAYMENT_METHOD,
          secondaryButtonText: textConstants.CONTACT_SUPPORT,
        };
        break;
      case GeneralPurchaseErrorEnum.NoAvailability:
        titles = {
          type: ErrorModalType.FLIGHTS_PRICE_QUOTE_HAS_NO_AVAILABILITY,
          title: textConstants.getNoAvailabilityTitle(numTravelers),
          subtitle: textConstants.getNoAvailabilitySubtitle(
            numTravelers,
            flightBookType
          ),
          icon: textConstants.UNABLED_ICON,
          // note: ideally, if the user only selected 1 PAX and still received NoAvailability, the secondary button shouldn't be displayed
          primaryButtonText: textConstants.SEARCH_AGAIN,
          secondaryButtonText:
            numTravelers > 1 &&
            flightBookType !== FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE
              ? textConstants.NO_AVAILABILITY_EDIT_TRAVELER_TEXT
              : "",
        };
        trackEvent({
          eventName: NO_AVAILABILITY_AIR,
          properties: {},
        });
        break;
      case PaymentErrorEnum.MissingNationality:
        titles = {
          title: (error as UncategorizedPurchaseError).title,
          subtitle: (error as UncategorizedPurchaseError).subtitle,
          primaryButtonText: textConstants.UPDATE_TRAVELERS,
        };
        break;
      case PaymentErrorEnum.UserCardNotFound:
        titles = textConstants.USER_CARD_ERROR_TITLES;
        break;
      case PaymentPurchaseErrorEnum.CardDeclined:
        titles = {
          title: textConstants.CARD_DECLINED,
          subtitle: textConstants.CARD_DECLINED_SUBTITLE,
          icon: textConstants.UNABLED_ICON,
          primaryButtonText: textConstants.EDIT_PAYMENT_METHOD,
        };
        break;
      case GeneralPurchaseErrorEnum.InActivity:
        hasSelectedSeats
          ? (titles = {
              type: ErrorModalType.FLIGHT_INACTIVITY,
              title: textConstants.INACTIVITY_TITLE,
              subtitle: textConstants.ERROR_WITH_SELECTED_SEATS_SUBTITLE,
              primaryButtonText: textConstants.CONTINUE,
            })
          : (titles = {
              type: ErrorModalType.FLIGHT_INACTIVITY,
              title: textConstants.INACTIVITY_TITLE,
              subtitle: textConstants.INACTIVITY_SUBTITLE,
              primaryButtonText: textConstants.TRY_AGAIN,
            });
        break;
      case FlightError.UnknownSabreAppError:
      case FlightError.MissingAirlineLocator:
      case FlightError.DuplicateBooking:
        if (hasSelectedSeats) {
          titles = {
            type: ErrorModalType.DUPLICATE_BOOKING,
            title: textConstants.DUPLICATE_BOOKING,
            subtitle: textConstants.DUPLICATE_BOOKING_SUBTITLE,
            primaryButtonText: textConstants.GO_TO_MY_TRIPS,
            secondaryButtonText: textConstants.SEARCH_AGAIN,
          };
        } else {
          titles = {
            type: ErrorModalType.PROVIDER_ERROR,
            title: textConstants.PROVIDER_ERROR_TITLE,
            subtitle: textConstants.PROVIDER_ERROR_SUBTITLE,
            primaryButtonText: textConstants.CHOOSE_ANOTHER_FLIGHT,
            secondaryButtonText: textConstants.TRY_AGAIN,
          };
        }
        break;
      case PaymentPurchaseErrorEnum.RedemptionFailure:
        titles = {
          title: textConstants.REDEMPTION_ISSUE,
          subtitle: textConstants.REDEMPTION_ISSUE_SUTITLE,
          primaryButtonText: textConstants.TRY_AGAIN,
          icon: textConstants.UNABLED_ICON,
        };
        break;
      case FlightError.LapInfantsUnsupported:
        titles = {
          title: textConstants.INVALID_PASSENGERS,
          subtitle: textConstants.LAP_INFANT_UNSUPPORTED,
          primaryButtonText: textConstants.CONTINUE_WITH_OWN_SEAT,
          secondaryButtonText: textConstants.CHOOSE_ANOTHER_FLIGHT,
          icon: textConstants.UNABLED_ICON,
        };
        break;
      case FlightError.PassengerNameTooShort:
        titles = {
          title: textConstants.NAME_TOO_SHORT,
          subtitle: textConstants.REVIEW_ID,
          primaryButtonText: textConstants.UPDATE_TRAVELER_DETAILS,
          icon: textConstants.CAUTION_ICON,
        };
        break;
      case FlightError.ExpiringPassport:
        titles = {
          title: textConstants.PASSENGER_ERROR,
          subtitle: (error as UncategorizedPurchaseError).subtitle,
          primaryButtonText: textConstants.UPDATE_TRAVELERS,
          icon: textConstants.CAUTION_ICON,
        };
        break;
      case FlightError.ExpiringPassport90Days:
        titles = {
          title: textConstants.PASSPORT_ERROR_90,
          subtitle: textConstants.PASSPORT_EXPIRATION_DATE_ERROR_SUBTITLE,
          primaryButtonText: textConstants.UPDATE_TRAVELER_DETAILS,
          icon: textConstants.CAUTION_ICON,
        };
        break;
      case FlightError.ExpiringPassport180Days:
        titles = {
          title: textConstants.PASSPORT_ERROR_180,
          subtitle: textConstants.PASSPORT_EXPIRATION_DATE_ERROR_SUBTITLE,
          primaryButtonText: textConstants.UPDATE_TRAVELER_DETAILS,
          icon: textConstants.CAUTION_ICON,
        };
        break;
      default:
        // note: by default, the returned ErrorTitles doesn't include a particular `type` for generic failures
        titles.subtitle = titles.subtitle;
    }
    return titles;
  });

  const realError = errorTitles.find(
    (e) => e.title !== textConstants.GENERIC_ERROR_TITLE
  );

  return {
    ...(realError ? realError : errorTitles[0]),
    agentSubtitle,
    agentTitle,
  };
};

/**
 * Check if an error requires updating travelers by examining the primary button text
 */
export const errorRequiresTravelerUpdate = (
  error: PurchaseError | FlightError
): boolean => {
  const errorCode = (error as UncategorizedPurchaseError).code || error;

  // Get the error titles for this specific error
  const errorTitles = getErrorTitlesFromPaymentError({
    errors: [errorCode as PurchaseError | FlightError],
    numTravelers: 1, // Use 1 as default since it doesn't affect UPDATE_TRAVELERS/UPDATE_TRAVELER_DETAILS
    flightBookType: undefined as any,
    hasSelectedSeats: false,
  });

  return (
    errorTitles.primaryButtonText === textConstants.UPDATE_TRAVELERS ||
    errorTitles.primaryButtonText === textConstants.UPDATE_TRAVELER_DETAILS
  );
};
